<template>
  <section class="i-sample-output">
    <div class="container">
      <div class="label mb-3">What you leave with</div>
      <h2 class="headline">An actual plan.<br />Not a framework.</h2>
      <p
        class="body-lg"
        style="color: var(--text-body); margin-top: 12px; max-width: 560px"
      >
        Most coaching sessions end with energy and no specifics. Here are real (anonymized) excerpts from action plans
        delivered after sessions — with permission from the clients.
      </p>

      <!-- Tabs -->
      <div class="plan-tabs">
        <div
          class="plan-tabs__tab"
          :class="{ 'plan-tabs__tab--active': activeTab === 'change' }"
          @click="activeTab = 'change'"
        >
          Career change <span class="plan-tabs__badge plan-tabs__badge--change">New direction</span>
        </div>
        <div
          class="plan-tabs__tab"
          :class="{ 'plan-tabs__tab--active': activeTab === 'upgrade' }"
          @click="activeTab = 'upgrade'"
        >
          Same field <span class="plan-tabs__badge plan-tabs__badge--upgrade">Level up</span>
        </div>
      </div>

      <!-- Plan A: Career Changer -->
      <div
        v-show="activeTab === 'change'"
        class="output-card output-card--tabbed"
      >
        <div class="output-card__header">
          <div>
            <div class="output-card__client-name">Alyssa R. — Customer Support Lead, $54K → $78K</div>
            <div class="output-card__client-meta">
              Goal: career change into UX research · Stuck and burned out for 2+ years
            </div>
          </div>
          <div class="output-card__badge">Real client · anonymized</div>
        </div>
        <div class="output-card__body">
          <div class="output-card__section-label">Personalized action plan — delivered same day</div>

          <div class="output-card__item">
            <div class="output-card__num">1</div>
            <div>
              <strong>Your path is real — here's why.</strong>
              <span
                >Your report shows high scores in empathy-driven problem solving and pattern recognition — the two core
                UX research competencies. Your 4 years handling customer escalations IS user research experience,
                reframed. You're not starting from zero.</span
              >
            </div>
          </div>
          <div class="output-card__item">
            <div class="output-card__num">2</div>
            <div>
              <strong>Target these roles specifically (not "UX Researcher").</strong>
              <span
                >Search for "Customer Insights Analyst" and "Voice of Customer Lead" — UX-adjacent roles that explicitly
                value support backgrounds. Mid-size SaaS companies, 100–500 employees. Apply to these 5 companies first:
                [specific list provided].</span
              >
            </div>
          </div>
          <div class="output-card__item">
            <div class="output-card__num">3</div>
            <div>
              <strong>One portfolio piece. 2 weeks. Here's the brief.</strong>
              <span
                >Take one customer escalation pattern from your current job. Write it up as a UX case study: problem,
                data, insight, recommendation. This single artifact closes the "no UX experience" gap. Template
                provided.</span
              >
            </div>
          </div>

          <div class="output-card__result-bar">
            <span class="output-card__result-label">Outcome: 5 months after session</span>
            <span class="output-card__result-val">$54K → $78K · Customer Insights Analyst, SaaS company</span>
          </div>
          <div class="output-card__disclaimer">
            Real client outcome. Results vary. Salary increase reflects a career change within 5 months. Confirmed via
            follow-up survey.
          </div>
        </div>
      </div>

      <!-- Plan B: Same-field Upgrade -->
      <div
        v-show="activeTab === 'upgrade'"
        class="output-card output-card--tabbed"
      >
        <div class="output-card__header">
          <div>
            <div class="output-card__client-name">Marcus D. — Marketing Coordinator, $47K → $68K</div>
            <div class="output-card__client-meta">
              Goal: promotion to senior role · Feeling overlooked and underpaid for 18 months
            </div>
          </div>
          <div class="output-card__badge">Real client · anonymized</div>
        </div>
        <div class="output-card__body">
          <div class="output-card__section-label">Personalized action plan — delivered same day</div>

          <div class="output-card__item">
            <div class="output-card__num">1</div>
            <div>
              <strong>You're applying to the wrong level — here's the fix.</strong>
              <span
                >Your resume reads "coordinator" but your actual work is manager-level. Rewrite the top 3 bullet points
                to lead with outcomes, not tasks: "Managed $180K campaign budget across 4 channels" not "Assisted with
                campaign planning." This single change moves you past the ATS filter for the roles you actually belong
                in.</span
              >
            </div>
          </div>
          <div class="output-card__item">
            <div class="output-card__num">2</div>
            <div>
              <strong>Your LinkedIn headline is invisible to recruiters.</strong>
              <span
                >Change from "Marketing Coordinator at Acme" to: "Marketing lead · $180K in managed campaigns · B2B
                SaaS." Recruiters search by skill keywords, not titles. This puts you in search results you're currently
                invisible in. Do this today — takes 5 minutes.</span
              >
            </div>
          </div>
          <div class="output-card__item">
            <div class="output-card__num">3</div>
            <div>
              <strong>Target these 8 companies (and skip the job boards).</strong>
              <span
                >Based on your profile, mid-stage B2B SaaS (Series A–B, 50–200 employees) is your sweet spot. Here are 8
                companies actively hiring marketing managers who value coordinator-to-manager promotions: [specific list
                provided]. Apply directly to the hiring manager on LinkedIn — not through the portal.</span
              >
            </div>
          </div>

          <div class="output-card__result-bar">
            <span class="output-card__result-label">Outcome: 3 months after session</span>
            <span class="output-card__result-val">$47K → $68K · Marketing Manager, B2B SaaS startup</span>
          </div>
          <div class="output-card__disclaimer">
            Real client outcome. Results vary. Salary increase reflects a promotion within 3 months. Confirmed via
            follow-up survey.
          </div>
        </div>
      </div>

      <!-- Inline CTA -->
      <div class="inline-cta">
        <div class="inline-cta-text">
          <strong>Get a plan like this, built for your situation.</strong>
          {{ timing }} minutes. One call. A written plan you keep.
          <div class="inline-proof">4.8/5 from 6,847 sessions · Full refund if it's not useful</div>
        </div>
        <a
          class="btn-inline"
          href="#book"
          @click.prevent="$emit('book')"
          >Book — ${{ pricing }}</a
        >
      </div>
      <div
        v-if="!paymentProcessing"
        class="skip"
        @click="$emit('skip')"
      >
        No thanks - I'm fine figuring it out the hard way
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue"

defineEmits(["book", "skip"])

// PROPS
defineProps({
  pricing: {
    type: String,
    default: "49"
  },
  timing: {
    type: String,
    default: "30"
  },
  paymentProcessing: {
    type: Boolean,
    default: false
  }
})

// REFS
const activeTab = ref("change")
</script>

<style lang="scss" scoped>
.i-sample-output {
  .plan-tabs {
    display: flex;
    gap: 8px;
    margin-top: 28px;
    margin-bottom: -4px;

    &__tab {
      padding: 10px 18px;
      font-size: 13px;
      font-weight: 500;
      color: var(--muted);
      background: var(--white);
      border: 1px solid var(--border);
      border-bottom: none;
      border-radius: var(--radius-sm) var(--radius-sm) 0 0;
      cursor: pointer;
      transition:
        color 0.15s,
        background 0.15s;
      position: relative;
      z-index: 1;

      &:not(&--active):hover {
        color: var(--text-body);
      }

      &--active {
        color: var(--purple);
        background: var(--white);
        font-weight: 600;
        border-color: var(--border);
        box-shadow: 0 1px 0 var(--white);
      }
    }

    &__badge {
      display: inline-block;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 2px 6px;
      border-radius: 4px;
      margin-left: 6px;
      vertical-align: 1px;

      &--change {
        background: var(--green-lt);
        color: var(--green-dark);
      }

      &--upgrade {
        background: var(--purple-pale);
        color: var(--purple);
      }
    }
  }

  .output-card {
    background: #fff;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    box-shadow: var(--card-shadow);

    &--tabbed {
      border-radius: 0 var(--radius) var(--radius) var(--radius);
    }

    &__header {
      background: var(--navy-mid);
      padding: 16px 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px;
    }

    &__client-name {
      font-size: 1rem;
      color: #f7f4ef;
      font-weight: 600;
    }

    &__client-meta {
      font-size: 0.75rem;
      color: #f7f4ef80;
    }

    &__badge {
      background: var(--purple);
      color: #fff;
      font-size: 0.7rem;
      font-weight: 700;
      padding: 3px 10px;
      border-radius: 8px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    &__body {
      padding: 28px 24px 24px;
    }

    &__section-label {
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--muted);
      margin-bottom: 14px;
    }

    &__item {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      margin-bottom: 14px;
      padding-bottom: 14px;
      border-bottom: 1px solid var(--border);
      font-size: 0.9rem;
      line-height: 1.55;

      &:last-of-type {
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;
      }

      strong {
        color: var(--text);
        font-weight: 600;
      }

      span {
        color: var(--text-body);
      }
    }

    &__num {
      flex-shrink: 0;
      width: 22px;
      height: 22px;
      background: var(--purple-pale);
      border: 1px solid var(--border-purple);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.7rem;
      font-weight: 800;
      color: var(--purple);
      margin-top: 1px;
    }

    &__result-bar {
      background: var(--green-lt);
      border: 1px solid #10b98140;
      border-radius: var(--radius-sm);
      padding: 12px 16px;
      margin-top: 18px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px;
    }

    &__result-label {
      font-size: 0.78rem;
      color: var(--green-dark);
      font-weight: 600;
    }

    &__result-val {
      font-size: 1rem;
      font-weight: 700;
      color: var(--green-dark);
    }

    &__disclaimer {
      font-size: 0.75rem;
      color: var(--muted);
      margin-top: 14px;
      font-style: italic;
    }
  }
}
</style>
